import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useT } from "@/lib/labels";

const Cover = ({ book }) => (
  <div className="relative aspect-[2/3] overflow-hidden border-[3px] border-double border-[#c9a227] bg-[#1a120c] p-1 shadow-[0_22px_40px_-24px_rgba(60,20,0,0.8)]">
    {book.cover_image
      ? <img src={imgUrl(book.cover_image)} alt={book.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      : <div className="w-full h-full grid place-items-center font-display text-3xl text-[#e6bb45] text-center p-4">{book.title}</div>}
    {book.blurb && (
      <div className="absolute inset-x-1 bottom-1 p-3 bg-[#1a120c]/85 text-[#f4efe2] text-sm italic opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
        {book.blurb}
      </div>
    )}
  </div>
);

export const BookCover = ({ book, index = 0 }) => {
  const label = (
    <div className="mt-4">
      <p className="eyebrow">Book {book.number}</p>
      <p className="font-heading text-lg font-bold text-[#2d1b0e] leading-snug">{book.title}</p>
    </div>
  );
  const t = useT();
  const style = { animationDelay: `${index * 90}ms` };
  if (book.kindle_url) {
    return (
      <a href={book.kindle_url} target="_blank" rel="noopener noreferrer" style={style} data-testid={`kindle-amazon-link-${book.slug}`} className="group block rise lift">
        <Cover book={book} />
        {label}
        {t("book.read_kindle") && <span className="mt-2 inline-flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-[0.2em] text-[#991b1b] group-hover:text-[#ea580c]">{t("book.read_kindle")} <ExternalLink className="w-3 h-3" /></span>}
      </a>
    );
  }
  return (
    <div style={style} data-testid={`series-book-card-${book.slug}`} className="group rise lift">
      <Cover book={book} />
      {label}
      {t("book.forthcoming") && <span className="mt-2 inline-block font-heading text-[11px] uppercase tracking-[0.2em] text-[#5e4a35]">{t("book.forthcoming")}</span>}
    </div>
  );
};

export const SeriesShowcase = ({ series }) => {
  const published = series.books?.length || 0;
  const t = useT();
  return (
    <div data-testid={`series-showcase-${series.slug}`} className="grid lg:grid-cols-12 gap-10 items-start">
      <div className="lg:col-span-4">
        <p className="eyebrow">A Chronicle in {series.planned_books || "many"} volumes</p>
        <h3 className="font-display text-3xl sm:text-4xl mt-2 text-[#2d1b0e]">{series.title}</h3>
        <p className="mt-4 text-lg leading-relaxed text-[#3b2c1f]">{series.description}</p>
        <p data-testid={`series-progress-${series.slug}`} className="mt-5 font-heading text-sm text-[#1e4976]">
          {published} {published === 1 ? "volume" : "volumes"} inscribed{series.planned_books ? ` of ${series.planned_books}` : ""}
        </p>
        {t("book.open_series") && <Link to={`/series/${series.slug}`} data-testid={`open-series-${series.slug}`} className="btn-ghost-illum mt-6">{t("book.open_series")}</Link>}
      </div>
      <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8">
        {series.books?.map((b, i) => <BookCover key={b.id} book={b} index={i} />)}
      </div>
    </div>
  );
};
